import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'

import { api, problemDetail } from '@/services/api'
import type { Overlay } from '@/types/api'

interface ObjectAccess {
  type: Overlay['type']
  title: string | null
  url?: string
  question?: { id: string; type: string; statement: string; options: { id: string; text: string }[] }
}
interface Feedback { is_correct: boolean; explanation: string | null; attempt_number: number }

export function OverlayDialog({ bookId, overlay, onClose }: {
  bookId: string; overlay: Overlay; onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [answer, setAnswer] = useState('')
  const attemptId = useRef<{value: string; id: string} | null>(null)
  const access = useQuery({
    queryKey: ['overlay', bookId, overlay.id],
    queryFn: async () => (await api.get<ObjectAccess>(`/reader/books/${bookId}/overlays/${overlay.id}/access`)).data,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const attempt = useMutation({
    mutationFn: async () => {
      if (attemptId.current?.value !== answer) attemptId.current = {value: answer, id: crypto.randomUUID()}
      return (await api.post<Feedback>(`/reader/books/${bookId}/overlays/${overlay.id}/attempts`, {
        value: answer, client_id: attemptId.current.id,
      })).data
    },
  })
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const node = dialog.current
    if (node?.showModal) node.showModal()
    else node?.setAttribute('open', '')
    return () => { node?.close?.(); previous?.focus() }
  }, [])
  const question = access.data?.question
  return createPortal(
    <dialog ref={dialog} className="reader-object-dialog" aria-labelledby="object-heading"
      onCancel={event => { event.preventDefault(); onClose() }}>
      <header><h2 id="object-heading">{overlay.title || 'Objeto do livro'}</h2>
        <button type="button" onClick={onClose}>Fechar</button></header>
      {access.isPending && <p role="status">Abrindo objeto…</p>}
      {access.isError && <div role="alert"><p>{problemDetail(access.error) || 'Não foi possível abrir este objeto.'}</p>
        <button type="button" onClick={() => { void access.refetch() }}>Tentar novamente</button></div>}
      {access.data?.type === 'interactive' && <iframe title={overlay.title || 'Objeto interativo'}
        src={access.data.url} sandbox="allow-scripts" referrerPolicy="no-referrer" />}
      {access.data?.type === 'audio' && <audio controls src={access.data.url}>
        <track kind="captions" />Seu navegador não suporta áudio.</audio>}
      {access.data?.type === 'link' && <p><a href={access.data.url} target="_blank" rel="noopener noreferrer">Abrir conteúdo externo em outra aba</a></p>}
      {question && <form onSubmit={event => { event.preventDefault(); attempt.mutate() }}>
        <fieldset disabled={attempt.isPending}><legend>{question.statement}</legend>
          {question.type === 'multiple_choice' && question.options.map(option => <label className="exercise-option" key={option.id}>
            <input type="radio" name="answer" value={option.id} checked={answer === option.id} onChange={() => {setAnswer(option.id); attempt.reset()}} required />{option.text}</label>)}
          {question.type === 'true_false' && [{id: 'true', text: 'Verdadeiro'}, {id: 'false', text: 'Falso'}].map(option => <label className="exercise-option" key={option.id}>
            <input type="radio" name="answer" value={option.id} checked={answer === option.id} onChange={() => {setAnswer(option.id); attempt.reset()}} required />{option.text}</label>)}
          {question.type === 'fill_blank' && <label>Sua resposta<input value={answer} onChange={event => {setAnswer(event.target.value); attempt.reset()}} maxLength={2000} required /></label>}
        </fieldset>
        <button type="submit" disabled={attempt.isPending || !answer.trim()}>{attempt.isPending ? 'Enviando…' : 'Responder'}</button>
        {attempt.isError && <p role="alert">{problemDetail(attempt.error) || 'Não foi possível enviar. Tente novamente.'}</p>}
        {attempt.data && <div role="status"><p>{attempt.data.is_correct ? 'Resposta correta!' : 'Ainda não. Tente novamente.'}</p><p>{attempt.data.explanation}</p></div>}
      </form>}
    </dialog>, document.body,
  )
}
